import { useQuery } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { Button as AriaButton, Disclosure, DisclosurePanel, Heading } from 'react-aria-components';
import { countriesQuery } from '../api/queries';
import type { AddressInput } from '../api/types';
import { useI18n } from '../i18n/I18nProvider';
import { ComboBoxField } from '../ui/ComboBox';
import fieldStyles from '../ui/Field.module.css';
import { TextField } from '../ui/TextField';
import { optionalText } from './forms';
import styles from './address.module.css';

export interface AddressState {
  country: string | null;
  governate: string;
  regionCity: string;
  street: string;
  buildingNumber: string;
  postalCode: string;
  floor: string;
  room: string;
  landmark: string;
  additionalInformation: string;
}

export function emptyAddress(country: string | null = 'EG'): AddressState {
  return {
    country,
    governate: '',
    regionCity: '',
    street: '',
    buildingNumber: '',
    postalCode: '',
    floor: '',
    room: '',
    landmark: '',
    additionalInformation: '',
  };
}

/** True when the user typed nothing in any address line (the country alone is not an address). */
export function isAddressBlank(address: AddressState): boolean {
  const { country: _country, ...lines } = address;
  return Object.values(lines).every((value) => value.trim() === '');
}

/** Request body shape; empty optional parts are omitted, required parts are sent as typed. */
export function toAddressInput(address: AddressState): AddressInput {
  return {
    country: address.country ?? '',
    governate: address.governate.trim(),
    regionCity: address.regionCity.trim(),
    street: address.street.trim(),
    buildingNumber: address.buildingNumber.trim(),
    postalCode: optionalText(address.postalCode),
    floor: optionalText(address.floor),
    room: optionalText(address.room),
    landmark: optionalText(address.landmark),
    additionalInformation: optionalText(address.additionalInformation),
  };
}

/**
 * ETA address lines (issuer branch or receiver). `prefix` matches the API's issue paths
 * ('' for branches, 'address.' for parties) so server errors land on the right input.
 */
export function AddressFields({
  value,
  onChange,
  errors,
  prefix = '',
  isRequired = true,
  excludeCountry,
}: {
  value: AddressState;
  onChange: (next: AddressState) => void;
  errors: Record<string, string>;
  prefix?: string;
  isRequired?: boolean;
  /** Country codes not allowed here (display filter only; the server validates). */
  excludeCountry?: string;
}) {
  const { t, pick } = useI18n();
  const countries = useQuery(countriesQuery);
  const set = (key: keyof AddressState) => (next: string) => onChange({ ...value, [key]: next });
  const field = (key: keyof AddressState) => `${prefix}${key}`;
  const optionalKeys = [
    'postalCode',
    'floor',
    'room',
    'landmark',
    'additionalInformation',
  ] as const;
  const optionalHasError = optionalKeys.some((key) => errors[field(key)]);

  return (
    <div className={styles.address}>
      <div className={fieldStyles.fieldRow}>
        <ComboBoxField
          label={t.address.country}
          fieldName={field('country')}
          options={(countries.data ?? [])
            .filter((country) => country.code !== excludeCountry)
            .map((country) => ({
              id: country.code,
              label: `${pick(country.nameAr, country.nameEn)} (${country.code})`,
            }))}
          value={value.country}
          onChange={(country) => onChange({ ...value, country })}
          errorMessage={errors[field('country')]}
          isRequired={isRequired}
        />
        <TextField
          label={t.address.governate}
          fieldName={field('governate')}
          value={value.governate}
          onChange={set('governate')}
          errorMessage={errors[field('governate')]}
          isRequired={isRequired}
        />
        <TextField
          label={t.address.regionCity}
          fieldName={field('regionCity')}
          value={value.regionCity}
          onChange={set('regionCity')}
          errorMessage={errors[field('regionCity')]}
          isRequired={isRequired}
        />
        <TextField
          label={t.address.street}
          fieldName={field('street')}
          value={value.street}
          onChange={set('street')}
          errorMessage={errors[field('street')]}
          isRequired={isRequired}
        />
        <TextField
          label={t.address.buildingNumber}
          fieldName={field('buildingNumber')}
          value={value.buildingNumber}
          onChange={set('buildingNumber')}
          errorMessage={errors[field('buildingNumber')]}
          isRequired={isRequired}
        />
      </div>
      <Disclosure defaultExpanded={optionalHasError} className={styles.more}>
        <Heading level={3} className={styles.moreHeading}>
          <AriaButton slot="trigger" className={styles.moreTrigger}>
            <ChevronDown size={16} aria-hidden="true" className={styles.moreChevron} />
            {t.address.more}
          </AriaButton>
        </Heading>
        <DisclosurePanel>
          <div className={fieldStyles.fieldRow}>
            <TextField
              label={t.address.postalCode}
              fieldName={field('postalCode')}
              value={value.postalCode}
              onChange={set('postalCode')}
              errorMessage={errors[field('postalCode')]}
              isOptional
            />
            <TextField
              label={t.address.floor}
              fieldName={field('floor')}
              value={value.floor}
              onChange={set('floor')}
              errorMessage={errors[field('floor')]}
              isOptional
            />
            <TextField
              label={t.address.room}
              fieldName={field('room')}
              value={value.room}
              onChange={set('room')}
              errorMessage={errors[field('room')]}
              isOptional
            />
            <TextField
              label={t.address.landmark}
              fieldName={field('landmark')}
              value={value.landmark}
              onChange={set('landmark')}
              errorMessage={errors[field('landmark')]}
              isOptional
            />
            <TextField
              label={t.address.additionalInformation}
              fieldName={field('additionalInformation')}
              value={value.additionalInformation}
              onChange={set('additionalInformation')}
              errorMessage={errors[field('additionalInformation')]}
              isOptional
            />
          </div>
        </DisclosurePanel>
      </Disclosure>
    </div>
  );
}

/** One-line address for tables and snapshots. */
export function formatAddress(parts: {
  street?: string | null;
  buildingNumber?: string | null;
  regionCity?: string | null;
  governate?: string | null;
  country?: string | null;
}): string {
  return [
    [parts.buildingNumber, parts.street].filter(Boolean).join(' '),
    parts.regionCity,
    parts.governate,
    parts.country,
  ]
    .filter(Boolean)
    .join('، ');
}
